import { Link } from 'react-router-dom';

import { ComportamentoHelpModal } from 'components/comportamento-help-modal';
import Recognition from 'components/recognition';
import RecognitionReceived from 'components/recognitionReceived';
import { useModal } from 'context/ModalContext';
import { FormatNumber } from 'utils/helpers';

import {
  Comportamento,
  comportamentosVisiveis,
  PontosPorComportamento,
  ROTULO_DO_COMPORTAMENTO,
  SubAbaReconhecimentos,
} from '../../perfil-calculos';
import { ICONE_DO_COMPORTAMENTO } from '../../icones-do-comportamento';
import { TITULO_DE_SECAO } from 'styles';

// Ordem do design nesta aba (difere da de "Pontos por comportamento" no Resumo).
/** Ordem e ícones das Dimensões do Comportamento; o rótulo vem de ROTULO_DO_COMPORTAMENTO. */
const DIMENSOES: { chave: Comportamento; rotulo: string; icone: string }[] = (
  ['colaboracao', 'foco', 'inovacao', 'resolutividade', 'lideranca'] as Comportamento[]
).map(chave => ({ chave, rotulo: ROTULO_DO_COMPORTAMENTO[chave], icone: ICONE_DO_COMPORTAMENTO[chave] }));

const CHIP_BASE = 'rounded-jj-pill px-[22px] font-display text-[13px] font-bold uppercase tracking-[0.005em]';
const CHIP_ATIVO = `${CHIP_BASE} border-0 bg-brand-blue py-[11px] text-brand-yellow`;
const CHIP_INATIVO = `${CHIP_BASE} border-2 border-solid border-brand-blue bg-brand-white py-[9px] text-brand-blue`;

interface MensagemEditada {
  message: string;
  id: string;
}

interface ReconhecimentosProps {
  sub: SubAbaReconhecimentos;
  onTrocarSub: (sub: SubAbaReconhecimentos) => void;
  pontos: PontosPorComportamento;
  ehGestor: boolean;
  filtro: object;
  mensagemEditada?: MensagemEditada | null;
  onEditarEnviado: (item: MensagemEditada) => void;
}

function DimensoesDoComportamento({ pontos, ehGestor }: { pontos: PontosPorComportamento; ehGestor: boolean }) {
  const { showModal } = useModal();

  return (
    <section>
      <h2 className={`${TITULO_DE_SECAO} mb-3`}>Dimensões do Comportamento</h2>
      <div className="rounded-2xl bg-surface-200 px-5 py-[26px] lg:px-7">
        <p className="m-0 mb-6 text-sm text-content">
          Acompanhe seus pontos recebidos em cada comportamento dentro dos{' '}
          <Link to="/destaques" className="font-bold text-brand-blue underline">
            Destaques da Cultura
          </Link>
        </p>
        <ul className="m-0 grid list-none grid-cols-[repeat(auto-fit,124px)] justify-center gap-5 p-0 lg:justify-between">
          {comportamentosVisiveis(DIMENSOES, ehGestor, pontos).map(({ chave, rotulo, icone }) => (
            <li key={chave} className="flex flex-col items-center gap-3.5">
              <button
                type="button"
                onClick={() => showModal('comportamento-help', chave)}
                aria-label={`Ver detalhes do comportamento ${rotulo}`}
                className="flex h-[124px] w-[124px] flex-col items-center justify-center gap-2.5 rounded-lg border-0 bg-surface-0 p-3 shadow-jj-01 transition hover:-translate-y-0.5"
              >
                <img src={icone} alt="" className="h-[58px] w-[58px] object-contain" />
                <span className="text-center text-[11px] leading-[1.2] text-content">{rotulo}</span>
              </button>
              <span className="w-[124px] rounded-jj-pill bg-brand-purple-light px-1.5 py-[11px] text-center font-display text-[15px] font-bold text-content">
                {FormatNumber(pontos[chave], false)} pontos
              </span>
            </li>
          ))}
        </ul>
      </div>
      <ComportamentoHelpModal />
    </section>
  );
}

export default function Reconhecimentos({
  sub,
  onTrocarSub,
  pontos,
  ehGestor,
  filtro,
  mensagemEditada,
  onEditarEnviado,
}: ReconhecimentosProps) {
  return (
    <div className="flex flex-col gap-[22px]">
      <div className="flex gap-3">
        <button
          type="button"
          aria-pressed={sub === 'RECEIVED'}
          className={sub === 'RECEIVED' ? CHIP_ATIVO : CHIP_INATIVO}
          onClick={() => onTrocarSub('RECEIVED')}
        >
          Recebidos
        </button>
        <button
          type="button"
          aria-pressed={sub === 'SENDED'}
          className={sub === 'SENDED' ? CHIP_ATIVO : CHIP_INATIVO}
          onClick={() => onTrocarSub('SENDED')}
        >
          Enviados
        </button>
      </div>

      {sub === 'RECEIVED' && (
        <>
          <DimensoesDoComportamento pontos={pontos} ehGestor={ehGestor} />
          <RecognitionReceived filter={filtro} />
        </>
      )}

      {sub === 'SENDED' && <Recognition message={mensagemEditada} filter={filtro} activeModal={onEditarEnviado} />}
    </div>
  );
}
